import { Table } from "antd";
import { snakeToCamelCase } from "common/utils";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useGetPaymentAllocationsListQuery } from "features/payments/paymentsAllocations";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

const MIN_ALLOCATION_AMOUNT = 0.01;
export default function PaymentAllocationsTable({ paymentIntentId }) {
  const { debtorId } = useParams();
  const { data: debtorAccounts } = useFetchDebtorAccountsQuery({ debtorId });
  const { data: paymentAllocations, isLoading } = useGetPaymentAllocationsListQuery({
    includePaymentIntentIds: [paymentIntentId],
    minTotalAmount: MIN_ALLOCATION_AMOUNT,
    pageSize: 1000, // Setting a high page size so we can fetch all allocations for this payment intent. We typically have less than 10 allocations per payment intent, so this should be fine.
  });
  const { data: constants } = useFetchBackendConstantsQuery();

  const accountsById = useMemo(() => {
    const map = new Map();
    debtorAccounts?.forEach((account) => {
      map.set(account.id, account);
    });
    return map;
  }, [debtorAccounts]);

  const columns = useMemo(
    () => [
      {
        title: "Account ID",
        dataIndex: "accountId",
        key: "accountId",
        fixed: "left",
        render: (accountId) => {
          const account = accountsById.get(accountId);
          return account?.externalId ?? accountId;
        },
      },
      {
        title: "Allocation Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        fixed: "left",
        render: (text) => `$${text}`,
      },
      ...(constants?.paymentCategories?.invoiceable?.map((category) => ({
        title: category.display,
        dataIndex: snakeToCamelCase(category.value),
        key: snakeToCamelCase(category.value),
        render: (text) => (text ? `$${text}` : "-"),
      })) ?? []),
      {
        title: "Overpayment",
        dataIndex: "overpayment",
        key: "overpayment",
        render: (text) => (text ? `$${text}` : "-"),
      },
      {
        title: "Owed to Agency",
        dataIndex: "owedToAgency",
        key: "owedToAgency",
        render: (text) => `$${text}`,
      },
      {
        title: "Owed to Creditor",
        dataIndex: "owedToCreditor",
        key: "owedToCreditor",
        render: (text) => `$${text}`,
      },
      {
        title: "Owed to Forwarding Entity",
        dataIndex: "owedToForwardingEntity",
        key: "owedToForwardingEntity",
        render: (text) => `$${text}`,
      },
      {
        title: "Creditor Fee Plan",
        dataIndex: "creditorFeePlan",
        key: "creditorFeePlan",
        render: (feePlan) => feePlan?.name ?? "-",
      },
    ],
    [accountsById, constants?.paymentCategories?.invoiceable],
  );

  return (
    <>
      <h4>Payment Allocations</h4>
      <Table
        loading={isLoading}
        rowKey="id"
        bordered
        rowClassName={() => "allocation-row"}
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={paymentAllocations?.results}
        pagination={false}
      />
    </>
  );
}
